import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiX, FiChevronDown, FiCheckSquare, FiHelpCircle, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { faturamentoService } from '../../services/api';

/**
 * BIEmployeeSelectSearch
 * Componente de seleção única com busca de Funcionários Winthor no padrão visual dos filtros de BI.
 */
export default function BIEmployeeSelectSearch({
  label = 'Funcionário (opcional)',
  placeholder = 'Buscar funcionário...',
  value,
  onChange,
  required = false
}) {
  const containerRef = useRef(null);
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const fetchEmployees = useCallback(async (search = '') => {
    try {
      setLoading(true);
      const res = await faturamentoService.listEmployees({ search, per_page: 500 });
      setOptions(res?.data?.employees || []);
    } catch (err) {
      console.error('Erro ao carregar funcionários:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees('');
  }, [fetchEmployees]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      if (searchTerm.trim().length > 1) {
        fetchEmployees(searchTerm);
      } else if (searchTerm.trim() === '') {
        fetchEmployees('');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, fetchEmployees]);

  const selectedEmployee = useMemo(() => {
    if (!value) return null;
    const strVal = String(value);
    // 1. Prioriza ID real do banco (sync_funcionarios.id) para evitar falsa colisão com código Winthor
    const byId = options.find((e) => String(e.id) === strVal);
    if (byId) return byId;
    // 2. Fallback para código Winthor (codfunc ou matrícula)
    return options.find((e) => String(e.codfunc) === strVal || String(e.matricula) === strVal) || null;
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const q = searchTerm.toLowerCase().trim();
    return options.filter((e) => {
      const nome = String(e.nome || '').toLowerCase();
      const cod = String(e.codfunc || '').toLowerCase();
      const matr = String(e.matricula || '').toLowerCase();
      const func = String(e.funcao || '').toLowerCase();
      const fil = String(e.filial_id || e.codfilial || '').toLowerCase();
      return (
        nome.includes(q) ||
        cod.includes(q) ||
        String(e.id || '').toLowerCase().includes(q) ||
        matr.includes(q) ||
        func.includes(q) ||
        fil.includes(q)
      );
    });
  }, [options, searchTerm]);

  const displayLabel = selectedEmployee
    ? `#${selectedEmployee.codfunc || selectedEmployee.matricula || selectedEmployee.id} - ${selectedEmployee.nome}${selectedEmployee.funcao ? ` • ${selectedEmployee.funcao}` : ''}`
    : value
    ? loading
      ? 'Carregando funcionário...'
      : `Funcionário #${value}`
    : '';

  const handleSelect = (emp) => {
    onChange(String(emp.id));
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = (e) => {
    if (e) e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div
      className="form-group-modern mb-3"
      style={{ position: 'relative', width: '100%', zIndex: isOpen ? 1054 : 1 }}
      ref={containerRef}
    >
      <label className="d-flex justify-content-between align-items-center">
        <span>
          <FiUsers className="me-1" style={{ color: '#8b5cf6' }} />
          {label} {required && <span className="text-danger">*</span>}
        </span>
      </label>

      <div className="bi-search-dropdown-container">
        <div className="bi-search-input-wrapper">
          <input
            type="text"
            className={`form-control-modern bi-search-input-box ${value ? 'is-selected' : ''}`}
            placeholder={
              isOpen
                ? 'Buscar funcionário por nome, matrícula ou código...'
                : displayLabel || placeholder
            }
            value={isOpen ? searchTerm : displayLabel}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onClick={() => {
              if (!isOpen) setIsOpen(true);
            }}
            readOnly={!isOpen}
          />

          <div className="bi-search-actions-inside">
            {value && (
              <span className="bi-selected-badge employee" title="Funcionário selecionado">
                <FiCheckSquare size={12} /> Funcionário
              </span>
            )}
            {(value || (isOpen && searchTerm)) && (
              <span
                className="bi-search-clear-icon"
                title="Limpar funcionário"
                onClick={handleClear}
              >
                <FiX size={15} />
              </span>
            )}
            <span
              className={`bi-search-chevron ${isOpen ? 'open' : ''}`}
              onClick={() => setIsOpen(!isOpen)}
              title={isOpen ? 'Fechar lista' : 'Abrir lista'}
            >
              <FiChevronDown size={16} />
            </span>
          </div>
        </div>

        {/* Modal Suspenso (Popover) */}
        {isOpen && (
          <div className="bi-search-popover">
            <div className="bi-popover-header">
              <span className="bi-popover-meta-title">
                <FiUsers size={13} style={{ color: '#8b5cf6' }} className="me-1" />
                Funcionários Winthor
              </span>
              <div className="bi-popover-header-actions">
                {value && (
                  <button
                    type="button"
                    className="bi-popover-clear-btn"
                    onClick={handleClear}
                  >
                    Desmarcar
                  </button>
                )}
                <span className="bi-popover-count-tag">
                  {filteredOptions.length} / {options.length}
                </span>
              </div>
            </div>

            <div className="bi-popover-list">
              {loading ? (
                <div className="bi-popover-empty">
                  <FiRefreshCw size={20} className="spin-ico d-block mx-auto mb-2 text-primary" />
                  Carregando funcionários...
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="bi-popover-empty">
                  <FiHelpCircle size={22} className="d-block mx-auto mb-1 opacity-50" />
                  Nenhum funcionário encontrado{searchTerm ? ` para "${searchTerm}"` : ''}.
                </div>
              ) : (
                filteredOptions.map((emp) => {
                  const isSelected = selectedEmployee
                    ? String(emp.id) === String(selectedEmployee.id)
                    : (String(emp.id) === String(value));
                  const initials = (emp.nome || 'FN').substring(0, 2).toUpperCase();

                  return (
                    <div
                      key={emp.id}
                      className={`bi-popover-item ${isSelected ? 'selected-employee' : ''}`}
                      onClick={() => handleSelect(emp)}
                    >
                      <div className="bi-popover-item-left">
                        <div className="bi-popover-avatar employee">{initials}</div>
                        <div className="bi-popover-info">
                          <span className="bi-popover-name" title={emp.nome}>
                            {emp.nome}
                          </span>
                          <div className="bi-popover-meta">
                            <span>
                              Cód: <strong>#{emp.codfunc || emp.id}</strong>
                            </span>
                            {emp.matricula && <span>• Matr: {emp.matricula}</span>}
                            {emp.funcao && <span>• {emp.funcao}</span>}
                            {(emp.filial_id || emp.codfilial) && (
                              <span>• Filial {emp.filial_id || emp.codfilial}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bi-popover-item-right">
                        {isSelected ? (
                          <span
                            className="d-flex align-items-center gap-1 fw-bold"
                            style={{ fontSize: '0.78rem', color: '#7c3aed' }}
                          >
                            <FiCheckSquare size={16} /> Selecionado
                          </span>
                        ) : (
                          <span className="bi-popover-target-val">
                            Cód. {emp.codfunc || emp.id}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
